// Core data model for a project. Pure data — no audio nodes here.
import { uid, pickColor } from "../util";

export type EffectKind =
  | "eq" | "compressor" | "delay" | "reverb" | "gain"
  | "lowpass" | "highpass" | "saturator" | "chorus" | "limiter" | "width"
  | "denoise" | "deverb" | "wam";

export interface EffectParam {
  [key: string]: number | string | boolean;
}

export interface Effect {
  id: string;
  kind: EffectKind;
  name: string;
  enabled: boolean;
  params: EffectParam;
  /** For WAM plugins: the module descriptor URL and serialized state. */
  wamUrl?: string;
  wamState?: any;
}

/** A MIDI note within a clip; start/dur in seconds in the clip's own note-space
 *  (independent of trimming — the clip window below decides what actually plays). */
export interface MidiNote { midi: number; start: number; dur: number; vel: number; }

export interface Clip {
  id: string;
  name: string;
  assetId: string;
  start: number; // seconds on the timeline
  duration: number; // seconds (visible length)
  offset: number; // seconds into the source asset
  gain: number; // linear
  fadeIn: number; // seconds
  fadeOut: number; // seconds
  /** Present → this is a MIDI clip (on an instrument track), played by the
   *  track's synth rather than from an audio buffer. */
  notes?: MidiNote[];
  /** Takes sharing a takeGroupId occupy the same slot; only the active one plays. */
  takeGroupId?: string;
  takeActive?: boolean; // undefined === active
}

export type TrackKind = "audio" | "instrument";

/** A track's sound generator (instrument tracks): the built-in subtractive synth
 *  (`params` layered over the preset defaults) or a sampled General-MIDI program. */
export type TrackInstrument =
  | { type: "synth"; preset: string; params?: Record<string, number | string> }
  | { type: "gm"; program: number; soundfont?: string }
  | { type: "drums"; kit?: string }
  | { type: "sampler"; mode: "pitched" | "kit"; rootMidi?: number; samples: { assetId: string; name: string; note?: number }[] }
  | { type: "soundfont"; name: string; soundfont?: string; url?: string }
  | { type: "wam"; url: string };

/** How a new recording interacts with existing audio on the same track. */
export type RecordMode = "comp" | "sos" | "overwrite";
export const RECORD_MODE_LABELS: Record<RecordMode, string> = {
  comp: "Comping (takes)",
  sos: "Sound on Sound",
  overwrite: "Overwrite",
};

export interface Track {
  id: string;
  name: string;
  kind: TrackKind;
  color: string;
  volume: number; // linear 0..~2
  pan: number; // -1..1
  mute: boolean;
  solo: boolean;
  armed: boolean;
  inputDeviceId: string | null;
  monitor: boolean;
  inputGain: number; // dB applied to the live input (monitor + recording)
  /** Which input channel(s) to record. Default "1" = mono from input 1 — the
   *  DAW convention for a mic. Recording "stereo" from a 2-input interface with
   *  only input 1 connected captures input 2's empty-preamp hiss as well. */
  inputChannel?: InputChannel;
  height: number; // px lane height
  compExpanded?: boolean; // show take drawer
  instrument?: TrackInstrument; // for kind === "instrument"
  clips: Clip[];
  fx: Effect[];
}

export type InputChannel = "1" | "2" | "stereo";

export const BASE_TRACK_HEIGHT = 120;
export const TAKE_ROW_HEIGHT = 28;

export interface AudioAsset {
  id: string;
  name: string;
  sampleRate: number;
  channels: number;
  length: number; // frames
  duration: number; // seconds
}

/** A user-saved effect preset (built-in effects), scoped to an effect kind so it
 *  only appears in that effect's preset list. Travels with the project. */
export interface FxPreset {
  id: string;
  kind: EffectKind;
  name: string;
  params: EffectParam;
}

/** A reusable step-sequencer pattern saved into the project (recall & edit later,
 *  distinct from baking it onto the timeline as a clip). */
export interface SavedSequence {
  id: string;
  name: string;
  def: TrackInstrument;
  steps: number;
  octave: number;
  tempo: number;
  active: string[]; // `${note}:${step}` cells that are on
}

export interface Project {
  id: string;
  name: string;
  tempo: number;
  beatsPerBar: number;
  masterVolume: number;
  masterFx: Effect[];
  recordMode: RecordMode; // how new takes interact with existing audio (project-wide)
  tracks: Track[];
  assets: AudioAsset[];
  sequences?: SavedSequence[]; // saved step-sequencer patterns
  fxPresets?: FxPreset[]; // user-saved effect presets
  loopEnabled: boolean;
  loopStart: number;
  loopEnd: number;
  createdAt: number;
  modifiedAt: number;
}

export function newClip(assetId: string, asset: AudioAsset, start = 0): Clip {
  return {
    id: uid("clip"),
    name: asset.name,
    assetId,
    start,
    duration: asset.duration,
    offset: 0,
    gain: 1,
    fadeIn: 0.005,
    fadeOut: 0.005,
  };
}

/** A MIDI clip (instrument track): no audio asset, holds notes instead. */
export function newMidiClip(start: number, duration: number, notes: MidiNote[] = [], name = "MIDI"): Clip {
  return {
    id: uid("clip"),
    name,
    assetId: "",
    start,
    duration,
    offset: 0,
    gain: 1,
    fadeIn: 0,
    fadeOut: 0,
    notes,
  };
}

/** The notes of a MIDI clip that fall inside its trim window `[offset,
 *  offset+duration)`, each re-based so `start` is relative to the clip's timeline
 *  `start`. This is what actually plays/renders — trimming or splitting a MIDI
 *  clip narrows the window without destroying notes (undo-friendly, like audio). */
export function windowedNotes(clip: Clip): MidiNote[] {
  if (!clip.notes) return [];
  const from = clip.offset, to = clip.offset + clip.duration;
  const out: MidiNote[] = [];
  for (const n of clip.notes) {
    if (n.start < from - 1e-6 || n.start >= to - 1e-6) continue;
    out.push({ midi: n.midi, vel: n.vel, start: n.start - from, dur: n.dur });
  }
  return out;
}

export function newTrack(index: number, kind: TrackKind = "audio"): Track {
  return {
    id: uid("trk"),
    name: kind === "audio" ? `Audio ${index + 1}` : `Inst ${index + 1}`,
    kind,
    color: pickColor(index),
    volume: 1,
    pan: 0,
    mute: false,
    solo: false,
    armed: false,
    inputDeviceId: null,
    monitor: false,
    inputGain: 0,
    height: 96,
    instrument: kind === "instrument" ? { type: "synth", preset: "lead" } : undefined,
    clips: [],
    fx: [],
  };
}

export function newProject(name = "Untitled Project"): Project {
  return {
    id: uid("proj"),
    name,
    tempo: 120,
    beatsPerBar: 4,
    masterVolume: 1,
    masterFx: [],
    recordMode: "comp",
    tracks: [newTrack(0), newTrack(1)],
    assets: [],
    loopEnabled: false,
    loopStart: 0,
    loopEnd: 0, // no region → the loop button repeats the whole song
    createdAt: Date.now(),
    modifiedAt: Date.now(),
  };
}

/** Bring a loaded/imported project up to date. Before loop regions could be set
 *  in the UI, every project carried an untouched 0–8 s default region, which made
 *  the loop button repeat only the first 8 seconds — treat that as "no region". */
export function normalizeProject(p: Project): Project {
  if (p.loopStart === 0 && p.loopEnd === 8) p.loopEnd = 0;
  return p;
}
